/**
 * Catálogo `estados_civil`: los ids son fijos en la base y aquí se les da nombre.
 *
 * «Casado(a) por sociedad legal» (8) entró el 2026-10-15 (migración
 * 20261015003217) y se comporta igual que «Casado(a) bienes mancomunados» (2):
 * pide y liga al cónyuge y exige acta de matrimonio. La edge function
 * `cliente-expediente` tiene la misma regla en `_shared/estado-civil.ts`.
 */
export const ESTADO_CIVIL = {
  SOLTERO: 1,
  CASADO_MANCOMUNADOS: 2,
  CASADO_SEPARADOS: 3,
  VIUDO: 4,
  UNION_LIBRE: 5,
  SEPARADO: 6,
  DIVORCIADO: 7,
  CASADO_SOCIEDAD_LEGAL: 8,
} as const;

/** Regímenes que obligan a ligar al cónyuge: mancomunados y sociedad legal. */
export const ESTADOS_CIVIL_REQUIERE_CONYUGE: readonly number[] = [
  ESTADO_CIVIL.CASADO_MANCOMUNADOS,
  ESTADO_CIVIL.CASADO_SOCIEDAD_LEGAL,
];

/**
 * Cuentan como casado: se les exige acta de matrimonio. El 2 va primero porque
 * `personasDelExpediente` lo usa como estado civil del cónyuge ligado.
 */
export const ESTADOS_CIVIL_CASADO: readonly number[] = [
  ESTADO_CIVIL.CASADO_MANCOMUNADOS,
  ESTADO_CIVIL.CASADO_SEPARADOS,
  ESTADO_CIVIL.CASADO_SOCIEDAD_LEGAL,
];

/** El formulario guarda el id como texto ('' sin elegir); la base, como número. */
type IdEstadoCivil = number | string | null | undefined;

function aId(id: IdEstadoCivil): number | null {
  if (id == null || id === '') return null;
  const n = Number(id);
  return Number.isInteger(n) ? n : null;
}

/** ¿Este estado civil pide y liga cónyuge? (2 y 8) */
export function estadoCivilRequiereConyuge(id: IdEstadoCivil): boolean {
  const n = aId(id);
  return n != null && ESTADOS_CIVIL_REQUIERE_CONYUGE.includes(n);
}

/** ¿Cuenta como casado para exigir el acta de matrimonio? (2, 3 y 8) */
export function esEstadoCivilCasado(id: IdEstadoCivil): boolean {
  const n = aId(id);
  return n != null && ESTADOS_CIVIL_CASADO.includes(n);
}

/**
 * Estado civil que se le escribe al cónyuge al ligarlo: el mismo del titular,
 * porque el régimen del matrimonio es uno solo. Si el titular no trae un estado
 * civil de casado (dato viejo inconsistente) se conserva el comportamiento de
 * antes: mancomunados.
 */
export function estadoCivilParaConyuge(idTitular: IdEstadoCivil): number {
  const n = aId(idTitular);
  return n != null && esEstadoCivilCasado(n) ? n : ESTADO_CIVIL.CASADO_MANCOMUNADOS;
}

/** Aviso cuando falta el cónyuge; `nombre` es el del catálogo para el estado elegido. */
export function mensajeFaltaConyuge(nombre: string | null | undefined): string {
  const t = nombre?.trim();
  return t
    ? `Debes seleccionar un cónyuge cuando el estado civil es '${t}'.`
    : 'Debes seleccionar un cónyuge para este estado civil.';
}
